import { useCallback, useMemo, useRef, useState } from 'react';

import { ChallengesProps } from './MainContent';
import { ChallengeNoData } from './noData';

import { Button, Modal } from '@/components';
import { TourneyList } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import {
  getDesafioAceptarTodos,
  requestTratarDesafio,
} from '@/services/challenges';
import { ChallengeStatus, ChallengeUser } from '@/types/challenges';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import { cn, STATUS_TOURNEY_INVITATION, TEXTS } from '@/utils';

const initialState = {
  showModalInvitations: false,
  showNewModal: false,
  nameUser: '',
  apellidoUser: '',
  idTourney: 0,
  tournamentDetails: null,
  processingInvitation: false,
  detailsFetched: false,
  isFromLink: false, // Nueva propiedad para identificar si la invitación viene de un link
  skipFetchDetails: false, // Nueva propiedad para evitar que se ejecute fetchTournamentDetails
};

export const ContentDesafio = ({
  invitationList,
  playingList,
  pendingList,
  refetchFather,
  hitsJugados,
}: ChallengesProps) => {
  const { isMobile } = useIsDesktop();
  const fetchedRef = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    render: () => <></>,
  });
  const [state, setState] = useState(initialState);
  const toast = useToast();
  const { userInitialData } = useAuth();

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setModalContent({
      title: '¿Querés aceptar todas las solicitudes de Desafíos 1 a 1?',
      render: () => (
        <div className="flex my-4 gap-2">
          <Button
            className="w-[49%] mt-4"
            type="button"
            variant={'secondary'}
            onClick={() => setIsModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            className="w-[49%] mt-4"
            type="button"
            variant={'primary'}
            onClick={() => aceptarTodosDesafios()}
          >
            Aceptar
          </Button>
        </div>
      ),
    });
  };

  const aceptarTodosDesafios = async () => {
    try {
      const responseDesafioAceptarTodos = await getDesafioAceptarTodos();
      if (responseDesafioAceptarTodos.estado) {
        setIsModalOpen(false);
        refetchFather?.();
      } else {
        toast.show(responseDesafioAceptarTodos.mensaje);
        setIsModalOpen(false);
      }
    } catch (error: any) {
      console.error(error);
      toast.show(error.mensaje);
      setIsModalOpen(false);
    }
  };

  const mapDesafiosToGenericItems = useCallback(
    (list: ChallengeUser[]) => {
      if (list.length === 0) return [];
      return list.map((item: ChallengeUser) => {
        return {
          id: item.idDesafio,
          title: `${item.nombreUsuarioRival} ${item.apellidoUsuarioRival}`,
          status:
            item.idEstadoDesafio === ChallengeStatus.PENDIENTE
              ? STATUS_TOURNEY_INVITATION.PENDING
              : STATUS_TOURNEY_INVITATION.REJECTED,
          subtitle: item.nombreEquipoUsuarioRival,
          disabled: userInitialData?.veda,
          estadistica:
            item.ganados || item.perdidos
              ? `GANO ${item.ganados ?? 0} - PERDIO ${item.perdidos ?? 0}`
              : '',
        };
      });
    },
    [userInitialData?.veda]
  );

  const invitationDataList = useMemo((): GenericItemTourneysData[] | [] => {
    return mapDesafiosToGenericItems(invitationList);
  }, [invitationList, mapDesafiosToGenericItems]);

  const playingDataList = useMemo((): GenericItemTourneysData[] | [] => {
    return mapDesafiosToGenericItems(playingList);
  }, [playingList, mapDesafiosToGenericItems]);

  const pendingDataList = useMemo((): GenericItemTourneysData[] | [] => {
    return mapDesafiosToGenericItems(pendingList);
  }, [pendingList, mapDesafiosToGenericItems]);

  const onHandleInvitations = async (action: ActionsTourneys) => {
    const dataUser = invitationList.find(
      (tourney: { idDesafio: number }) => tourney.idDesafio === action.id
    );
    setState({
      ...initialState,
      showModalInvitations: true,
      nameUser: dataUser?.nombreUsuarioRival ?? 'falta servicio',
      apellidoUser: dataUser?.apellidoUsuarioRival ?? 'falta servicio',
      idTourney: action.id,
      detailsFetched: true,
      showNewModal: false,
      isFromLink: false,
      skipFetchDetails: true,
    });
    fetchedRef.current = true;
  };

  // Función para manejar invitaciones desde la lista "Te invitaron a participar"
  const onHandleListInvitation = async (action: string, idDesafio: number) => {
    setState((prevState) => ({
      ...prevState,
      processingInvitation: true,
    }));

    try {
      // Para invitaciones en la lista "Te invitaron a participar"
      const responseTratarDesafio = await requestTratarDesafio(
        idDesafio,
        action === 'accept' ? 'aceptar' : 'rechazar'
      );
      if (responseTratarDesafio.estado) {
        if (action === 'accept') {
          toast.show('Aceptaste correctamente el desafío.');
        }
      } else {
        // Mostrar el mensaje específico del backend
        if (responseTratarDesafio.mensaje) {
          toast.show(responseTratarDesafio.mensaje);
        } else {
          toast.show(TEXTS.defaultError);
        }
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    } finally {
      setState(initialState);
      fetchedRef.current = false; // Resetear el ref
      refetchFather?.();
    }
  };

  const handleCloseModal = () => {
    setState(initialState);
    fetchedRef.current = false;
  };

  return (
    <>
      {invitationDataList.length === 0 &&
        playingDataList.length === 0 &&
        pendingDataList.length === 0 &&
        hitsJugados && <ChallengeNoData />}
      {invitationDataList.length > 0 && (
        <TourneyList
          className="lg:mt-9 mt-6"
          classNames={{
            subtitle: 'text-gdt-gray-2',
            titleContainer: 'px-4 md:px-0',
          }}
          context={EnumContextTourneys.MainContentHome}
          handleSubmit={(params) => {
            if (isMobile) {
              params.action && onHandleInvitations(params);
            } else {
              params.action && onHandleListInvitation(params.action, params.id);
            }
          }}
          headerAction={
            <Button
              className={cn('font-bold mb-3', { 'font-bold mb-8': isMobile })}
              disabled={userInitialData?.veda}
              type="button"
              variant="text"
              onClick={() => handleOpenModal()}
            >
              Aceptar todos
            </Button>
          }
          list={invitationDataList}
          paginator={true}
          textCentered={false}
          title={
            <>
              {`${isMobile ? 'Te desafiaron' : 'Te desafiaron.'}`}
              {isMobile && <br />}
              <span style={{ color: 'gray' }}>
                {` Tenés ${invitationDataList.length} ${
                  invitationDataList.length === 1 ? 'desafío' : 'desafíos'
                } por confirmar.`}
              </span>
            </>
          }
          variant="two-actions"
        />
      )}

      {playingDataList.length > 0 && (
        <TourneyList
          className="lg:mt-9 mt-6"
          classNames={{
            titleContainer: 'px-4 md:px-0',
          }}
          context={EnumContextTourneys.MainContentHome}
          list={playingDataList}
          paginator={true}
          title={
            <>
              {`${
                isMobile ? 'Desafios próxima fecha' : 'Desafios próxima fecha.'
              }`}
              {isMobile && <br />}
              <span style={{ color: 'gray' }}>
                {` Tenés ${playingDataList.length} ${
                  playingDataList.length === 1
                    ? 'desafío confirmado'
                    : 'desafíos confirmados.'
                }`}
              </span>
            </>
          }
          variant="no-option"
        />
      )}

      {pendingDataList.length > 0 && (
        <TourneyList
          className="lg:mt-9 mt-6"
          classNames={{
            titleContainer: 'px-4 md:px-0',
          }}
          context={EnumContextTourneys.MainContentHome}
          list={pendingDataList}
          paginator={true}
          title={`Esperando aceptación`}
          variant="show-status"
        />
      )}

      <Modal
        controlledOnClose={() => setIsModalOpen(false)}
        isControlledOpen={isModalOpen}
        render={modalContent.render}
        showCloseButton={true}
        size="xs"
        title={modalContent.title}
        titleClassName="text-lg text-gdt-gray ml-5"
        variant="controlled"
      />

      {/* Modal para invitaciones desde el listado) */}
      <Modal
        controlledOnClose={handleCloseModal}
        isControlledOpen={
          state.showModalInvitations &&
          isMobile &&
          !state.showNewModal &&
          // state.idTourney > 0 &&
          !state.isFromLink &&
          !state.processingInvitation
        }
        render={() => {
          return (
            <div className="text-center mt-2">
              <p className="text-base font-medium">
                <strong>
                  {state.nameUser} {state.apellidoUser} te desafió
                </strong>
              </p>
              <div className="flex space-x-2 my-10">
                <Button
                  className="flex-1"
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    onHandleListInvitation('reject', state.idTourney);
                  }}
                >
                  Rechazar
                </Button>
                <Button
                  className="flex-1"
                  type="button"
                  variant="primary"
                  onClick={() => {
                    onHandleListInvitation('accept', state.idTourney);
                  }}
                >
                  Aceptar
                </Button>
              </div>
            </div>
          );
        }}
        variant="controlled"
      />
    </>
  );
};
